/** Two-leaf vehicle gate for the loading-yard perimeter. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const POST_STEEL = 0x848b90;
const WIRE_STEEL = 0x9aa1a6;

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function tube(radius: number, length: number, material: THREE.Material, name: string): THREE.Mesh {
  return part(new THREE.CylinderGeometry(radius, radius, length, 6, 1, true), material, name);
}

function leaf(name: string, direction: -1 | 1, steel: THREE.Material, wireSteel: THREE.Material): THREE.Group {
  const pivot = new THREE.Group();
  pivot.name = name;
  // With each leaf closed at rotation.y = 0, a positive left rotation and a
  // negative right rotation both swing their inward geometry toward -Z. The signs
  // are intentionally opposite because their local inward X directions are opposite.
  const frame = new THREE.Group();
  frame.name = `${name}-frame`;
  const length = 2.00;
  const centre = direction * length / 2;
  for (const [x, y, width, height, partName] of [
    [centre, 0.025, length, 0.05, 'bottom-frame'],
    [centre, 1.975, length, 0.05, 'top-frame'],
    [0, 1.0, 0.05, 2.0, 'hinge-stile'],
    [direction * length, 1.0, 0.05, 2.0, 'meeting-stile'],
  ] as const) {
    const bar = part(new THREE.BoxGeometry(width, height, 0.05), steel, `${name}-${partName}`);
    bar.position.set(x, y, 0);
    frame.add(bar);
  }
  pivot.add(frame);

  for (const y of [0.50, 1.00, 1.50]) {
    const rail = tube(0.015, 1.90, steel, `${name}-rail-${y.toFixed(2)}`);
    rail.rotation.z = Math.PI / 2;
    rail.position.set(centre, y, 0);
    pivot.add(rail);
  }
  for (let index = 1; index < 8; index += 1) {
    const vertical = tube(0.006, 1.90, wireSteel, `${name}-wire-${index}`);
    vertical.position.set(direction * index * 0.25, 1.0, 0);
    pivot.add(vertical);
  }
  const brace = part(new THREE.BoxGeometry(0.035, 2.76, 0.035), steel, `${name}-diagonal-brace`);
  brace.rotation.z = -direction * Math.atan2(2, 2);
  brace.position.set(centre, 1.0, 0);
  pivot.add(brace);
  return pivot;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'perimeter-gate';
  const steel = surface('metal', POST_STEEL);
  const wireSteel = surface('metal', WIRE_STEEL);

  for (const x of [-2.05, 2.05]) {
    const post = part(new THREE.BoxGeometry(0.10, 2.35, 0.10), steel, `hinge-post-${x < 0 ? 'left' : 'right'}`);
    post.position.set(x, 1.175, 0);
    group.add(post);
  }
  const left = leaf('leaf-left', 1, steel, wireSteel);
  left.position.set(-2.05, 0, 0);
  const right = leaf('leaf-right', -1, steel, wireSteel);
  right.position.set(2.05, 0, 0);
  group.add(left, right);

  const bolt = tube(0.012, 0.32, steel, 'right-leaf-drop-bolt');
  bolt.position.set(-2.0, 0.24, 0.035);
  right.add(bolt);
  const socket = part(new THREE.BoxGeometry(0.12, 0.01, 0.12), steel, 'ground-socket-plate');
  socket.position.set(0, 0.005, 0);
  group.add(socket);
  return group;
}

export const perimeterGateAsset: AssetDefinition = {
  id: 'perimeter-gate',
  label: 'Perimeter vehicle gate',
  description: 'Closed two-leaf galvanized vehicle gate on real hinge pivots.',
  footprint: { width: 4.30, height: 2.35, length: 0.12 },
  create: build,
};
